/**
 * The Sales Metros page's (GOTU-6009) client-side list operations: resolving
 * each metro's admin ids to names, and the search.
 *
 * Why here and not on the server: organization-service's list filters by metro
 * name and zip but not by admin, and auth-service has no `ids` filter to name a
 * metro's admins with. The page therefore reads both lists whole and joins,
 * searches and pages them here. When either service grows the missing filter,
 * this file is the one place to swap it in.
 */

/** "Metro pagination = 15" (GOTU-6009). */
export const SALES_METROS_PAGE_SIZE = 15;

const ZIP_PATTERN = /^\d{5}$/;

/**
 * Whether a search term is also a zip lookup. Only a whole 5-digit zip is sent
 * to the server — it 422s anything else — so "331" searches names alone.
 */
export const isZipSearch = (term) => ZIP_PATTERN.test(term);

/**
 * Joins each metro's assigned ids to the admin list.
 *
 * An id the admin list does not carry keeps its place with a null name rather
 * than being dropped: organization-service's mirror can lag auth-service, and
 * an edit that started from a silently shortened list would unassign someone.
 *
 * @param {import('./types/SalesMetro').ISalesMetro[]} metros - Server order
 * @param {import('../Users/types/User').IGotuAdminOptionViewEntity[]} admins
 * @returns {import('./types/SalesMetro').ISalesMetroRow[]}
 */
export const toSalesMetroRows = (metros, admins) => {
  const namesById = new Map(admins.map((admin) => [admin.internalUserId, admin.name]));

  return metros.map((metro) => ({
    id: metro.id,
    name: metro.name,
    assignedInternalUserIds: metro.assignedInternalUserIds,
    admins: metro.assignedInternalUserIds.map((internalUserId) => ({
      internalUserId,
      name: namesById.get(internalUserId) ?? null,
    })),
  }));
};

const includesTerm = (text, needle) => Boolean(text) && text.toLowerCase().includes(needle);

/**
 * The rows the search matches: a case-insensitive substring of the metro name
 * or of any assigned admin's name, plus the metro that owns the term when it
 * is a zip. Order is preserved — the rows arrive sorted by name from the
 * service. An empty term matches everything.
 *
 * @param {import('./types/SalesMetro').ISalesMetroRow[]} rows
 * @param {Object} [options]
 * @param {string} [options.search] - Already trimmed
 * @param {string|null} [options.zipMatchId] - The metro owning the term, if any
 */
export const filterSalesMetroRows = (rows, { search = '', zipMatchId = null } = {}) => {
  if (!search) return rows;

  const needle = search.toLowerCase();

  return rows.filter(
    (row) =>
      row.id === zipMatchId ||
      includesTerm(row.name, needle) ||
      row.admins.some((admin) => includesTerm(admin.name, needle)),
  );
};
